// hooks/useCommandExecution.ts
/**
 * Runs plan commands (the `/`-style vocabulary shared by the chat, the
 * StudentCommandBar and the admin CommandPalette).
 *
 * The CommandContext itself is built by `useCommandContext` — the single
 * wiring both command surfaces consume.
 */

import { useCallback } from 'react';
import { parseCommand } from '../components/CommandPalette/utils/commandParser';
import { executeCommand, type CommandResult } from '../components/CommandPalette/utils/commandExecutor';
import { useInstitutionConfig } from '../contexts/InstitutionConfigContext';
import { useCommandContext, type UseCommandContextParams } from './useCommandContext';
import { getErrorMessage } from '../utils/errorUtils';

// Re-exported for callers that type against the command-op subsets.
export type { CommandPlanOps, CommandCourseOps } from './useCommandContext';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Toast handler types
 */
export type ShowSuccessHandler = (message: string) => void;
export type ShowErrorHandler = (message: string) => void;

/**
 * Parameters for the useCommandExecution hook
 */
export interface UseCommandExecutionParams extends UseCommandContextParams {
  showSuccess: ShowSuccessHandler;
  showError: ShowErrorHandler;
}

/**
 * Command execution result
 */
export interface CommandExecutionResult {
  success: boolean;
  message: string;
}

export interface CommandRunOptions {
  /**
   * Surface the result as a toast. True for the chat / command-bar paths,
   * where there is nowhere else for the result to land; false for the
   * CommandPalette, which prints the result into its own terminal output.
   */
  notify?: boolean;
}

/**
 * Return type for the useCommandExecution hook
 */
export interface UseCommandExecutionReturn {
  /** Run a command and toast the result. */
  executeCommandFromChat: (commandString: string) => Promise<CommandExecutionResult>;
  /** Run a command, choosing whether the result is toasted. */
  runCommand: (
    commandString: string,
    options?: CommandRunOptions
  ) => Promise<CommandExecutionResult>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to handle command execution from the chat interface and command bars.
 */
export function useCommandExecution({
  academicPlan,
  planOps,
  courses,
  courseOps,
  showSuccess,
  showError
}: UseCommandExecutionParams): UseCommandExecutionReturn {
  const { config: institutionConfig } = useInstitutionConfig();
  const context = useCommandContext({ academicPlan, planOps, courses, courseOps });

  const runCommand = useCallback(async (
    commandString: string,
    { notify = true }: CommandRunOptions = {}
  ): Promise<CommandExecutionResult> => {
    try {
      // Phase 6.4: institution-aware course-code pattern via slug.
      const parsed = parseCommand(commandString, institutionConfig.slug);

      // Parse errors are returned, never toasted — callers render them where
      // the command was typed (command bar inline, palette terminal).
      if ('error' in parsed) {
        return { success: false, message: parsed.error };
      }

      const result: CommandResult = await executeCommand(parsed, context);

      if (notify) {
        if (result.success) {
          showSuccess(result.message);
        } else {
          showError(result.message);
        }
      }

      return result;

    } catch (err) {
      const errorMessage = `Command execution error: ${getErrorMessage(err)}`;
      if (notify) showError(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, [context, showSuccess, showError, institutionConfig.slug]);

  const executeCommandFromChat = useCallback(
    (commandString: string): Promise<CommandExecutionResult> => runCommand(commandString),
    [runCommand]
  );

  return { executeCommandFromChat, runCommand };
}
